/**
 * Cadastro de produto — formulário do editor em funções PURAS (testáveis com
 * `node --test`, sem React): seções, DTO ↔ rascunho, diff, validação no
 * cliente (espelho da B.11 da spec, com as emendas E2), payloads do POST/PUT,
 * tradução dos erros do servidor, pendências e dicas do `bling_snapshot`.
 *
 * O rascunho guarda STRINGS de input (vírgula decimal, máscara de NCM/CEST),
 * booleanos nos switches e `tax_origin` como `''|'0'..'8'`. A conversão para o
 * wire (snake_case, números JSON, `null` para vazio) acontece só nos
 * `Build*Payload`.
 */

import {
    CASE_GTIN_LENGTHS,
    COST_PRICE_DECIMALS,
    COST_PRICE_MAX,
    DEFAULT_LOW_STOCK_THRESHOLD,
    DEFAULT_PRODUCT_UNIT,
    FCI_REQUIRED_ORIGINS,
    GTIN_LENGTHS,
    LOW_STOCK_THRESHOLD_MAX,
    PRODUCT_TITLE_MAX,
    PRODUCT_UNIT_MAX,
    SALE_PRICE_DECIMALS,
    SALE_PRICE_MAX,
    SUPPLIER_CODE_MAX,
    SUPPLIER_DESCRIPTION_MAX,
    TAX_GTIN_LENGTHS,
    UNITS_PER_CASE_MAX,
    UNITS_PER_CASE_MIN,
    UNITS_PER_PALLET_MAX,
    UNITS_PER_PALLET_MIN,
    WEIGHT_DECIMALS,
    WEIGHT_MAX_KG,
    WEIGHT_SENTINEL_KG,
} from '../../constants/productCatalog';
import { MarginPct } from '../../utils/aggregation';
import { BrandMatchKey, ResolveCanonicalBrand } from '../../utils/brands';
import { FormatDecimalBr, ParseDecimalInput, ParseIntegerInput } from '../../utils/format';
import {
    DeriveGtin13FromGtin14,
    FormatCest,
    FormatNcm,
    HasAtMostDecimals,
    NormalizeFciNumber,
    NormalizeSkuInput,
    OnlyDigits,
    ValidateCest,
    ValidateFci,
    ValidateGtin,
    ValidateNcm,
    ValidateNewSku,
} from '../../utils/productCodes';

// ── Seções ───────────────────────────────────────────────────────────────────

/**
 * Seções do editor, na ordem da página. `id` é também o `section` das
 * pendências do servidor (`pending[].section`). `composicao` só existe em KIT.
 */
export const EDITOR_SECTIONS = [
    {
        id: 'geral',
        label: 'Dados gerais',
        fields: ['title', 'sku', 'sale_price', 'unit', 'brand', 'format', 'status'],
    },
    { id: 'codigos', label: 'Códigos de barras', fields: ['ean', 'tax_ean', 'case_gtin', 'units_per_case'] },
    { id: 'logistica', label: 'Peso e pallet', fields: ['weight_net_kg', 'weight_gross_kg', 'units_per_pallet'] },
    { id: 'composicao', label: 'Composição', fields: [] },
    { id: 'estoque', label: 'Estoque e armazém', fields: ['low_stock_threshold', 'is_bulky', 'track_supplier_lot'] },
    { id: 'fornecedor', label: 'Fornecedor', fields: ['supplier_code', 'supplier_description', 'cost_price'] },
    { id: 'tributacao', label: 'Tributação', fields: ['tax_origin', 'fci_number', 'ncm', 'cest'] },
];

/** Seções visíveis: `composicao` só com formato KIT. */
export function VisibleSections(format) {
    return EDITOR_SECTIONS.filter((section) => section.id !== 'composicao' || format === 'KIT');
}

const SECTION_BY_FIELD = new Map(
    EDITOR_SECTIONS.flatMap((section) => section.fields.map((field) => [field, section.id])),
);

/** Seção de um campo do formulário (`null` quando não pertence a nenhuma). */
export function SectionOfField(field) {
    return SECTION_BY_FIELD.get(field) ?? null;
}

/** Id DOM da seção (âncora do scroll-spy e de `#<secao>` na URL). */
export function SectionDomId(sectionId) {
    return `secao-${sectionId}`;
}

/** Id DOM do input de um campo (foco no primeiro erro). */
export function FieldDomId(field) {
    return `produto-campo-${field}`;
}

// ── Campos ───────────────────────────────────────────────────────────────────

export const PRODUCT_FORM_FIELDS = EDITOR_SECTIONS.flatMap((section) => section.fields);

/** Atributos do WMS: gravados por `PUT /warehouse/products/:id/attributes`. */
export const WMS_ATTRIBUTE_FIELDS = ['is_bulky', 'track_supplier_lot'];

/** Nunca vão no PUT do cadastro: SKU e formato são imutáveis aqui. */
const UPDATE_EXCLUDED_FIELDS = new Set(['sku', 'format', ...WMS_ATTRIBUTE_FIELDS]);

/** Campos do POST (sem `format` — o servidor grava SIMPLE — e sem WMS). */
const CREATE_FIELDS = PRODUCT_FORM_FIELDS.filter(
    (field) => field !== 'format' && field !== 'sku' && !WMS_ATTRIBUTE_FIELDS.includes(field),
);

const UPPER_TEXT_FIELDS = new Set(['title', 'unit']);
const TRIM_TEXT_FIELDS = new Set(['brand', 'supplier_code', 'supplier_description']);
const DIGIT_FIELDS = new Set(['ean', 'tax_ean', 'case_gtin', 'ncm', 'cest']);
const DECIMAL_FIELDS = new Set(['sale_price', 'cost_price', 'weight_net_kg', 'weight_gross_kg']);
const INTEGER_FIELDS = new Set(['units_per_case', 'units_per_pallet', 'low_stock_threshold']);
const BOOLEAN_FIELDS = new Set(WMS_ATTRIBUTE_FIELDS);

export const EMPTY_PRODUCT_FORM = Object.freeze({
    title: '',
    sku: '',
    sale_price: '',
    unit: DEFAULT_PRODUCT_UNIT,
    brand: '',
    format: 'SIMPLE',
    status: 'ACTIVE',
    ean: '',
    tax_ean: '',
    case_gtin: '',
    units_per_case: '',
    weight_net_kg: '',
    weight_gross_kg: '',
    units_per_pallet: '',
    low_stock_threshold: '',
    is_bulky: false,
    track_supplier_lot: false,
    supplier_code: '',
    supplier_description: '',
    cost_price: '',
    tax_origin: '',
    fci_number: '',
    ncm: '',
    cest: '',
});

function text(value) {
    return value === null || value === undefined ? '' : String(value);
}

/** Maiúsculas pt-BR com espaços colapsados (título, unidade). */
export function UpperCollapse(value) {
    return String(value ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLocaleUpperCase('pt-BR');
}

function weightToInput(value) {
    const number = Number(value);
    if (value === null || value === undefined || value === '' || !Number.isFinite(number)) {
        return '';
    }
    // O sentinela 0,001 é "não informado": aparece VAZIO.
    if (number <= WEIGHT_SENTINEL_KG) {
        return '';
    }
    return FormatDecimalBr(number, { maxFractionDigits: WEIGHT_DECIMALS, grouping: false });
}

/**
 * DTO do backend (B.2) → rascunho do formulário (strings pt-BR). DECIMAL pode
 * chegar como string (mysql2) ou número: a conversão aceita os dois.
 */
export function ProductFormFromDto(dto) {
    if (!dto) {
        return { ...EMPTY_PRODUCT_FORM };
    }
    const hasOrigin = dto.tax_origin !== null && dto.tax_origin !== undefined && dto.tax_origin !== '';
    return {
        title: text(dto.title),
        sku: text(dto.sku),
        sale_price: FormatDecimalBr(dto.sale_price, {
            minFractionDigits: SALE_PRICE_DECIMALS,
            maxFractionDigits: SALE_PRICE_DECIMALS,
            grouping: false,
        }),
        unit: text(dto.unit),
        brand: text(dto.brand),
        format: dto.format === 'KIT' ? 'KIT' : 'SIMPLE',
        status: dto.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
        ean: OnlyDigits(dto.ean),
        tax_ean: OnlyDigits(dto.tax_ean),
        case_gtin: OnlyDigits(dto.case_gtin),
        units_per_case: text(dto.units_per_case),
        weight_net_kg: weightToInput(dto.weight_net_kg),
        weight_gross_kg: weightToInput(dto.weight_gross_kg),
        units_per_pallet: text(dto.units_per_pallet),
        low_stock_threshold: text(dto.low_stock_threshold),
        is_bulky: Boolean(dto.is_bulky),
        track_supplier_lot: Boolean(dto.track_supplier_lot),
        supplier_code: text(dto.supplier_code),
        supplier_description: text(dto.supplier_description),
        cost_price: FormatDecimalBr(dto.cost_price, {
            minFractionDigits: 2,
            maxFractionDigits: COST_PRICE_DECIMALS,
            grouping: false,
        }),
        tax_origin: hasOrigin ? String(dto.tax_origin) : '',
        fci_number: text(dto.fci_number),
        ncm: FormatNcm(dto.ncm),
        cest: FormatCest(dto.cest),
    };
}

/**
 * Forma CANÔNICA de um valor do rascunho, para o diff: "10,50" e "10.5" são o
 * mesmo preço; "2309.10.00" e "23091000" o mesmo NCM; estoque mínimo vazio é
 * o padrão 20. Valor inválido vira `invalid:<texto>` (sempre "alterado").
 */
export function NormalizeFormValue(field, value) {
    if (BOOLEAN_FIELDS.has(field)) {
        return Boolean(value);
    }
    const raw = String(value ?? '').trim();
    if (field === 'sku') {
        return NormalizeSkuInput(raw);
    }
    if (field === 'fci_number') {
        return NormalizeFciNumber(raw) ?? '';
    }
    if (UPPER_TEXT_FIELDS.has(field)) {
        return UpperCollapse(raw);
    }
    if (DIGIT_FIELDS.has(field)) {
        return OnlyDigits(raw);
    }
    if (DECIMAL_FIELDS.has(field)) {
        const parsed = ParseDecimalInput(raw);
        if (parsed === null) {
            return '';
        }
        return Number.isNaN(parsed) ? `invalid:${raw}` : String(parsed);
    }
    if (INTEGER_FIELDS.has(field)) {
        const parsed = ParseIntegerInput(raw);
        if (parsed === null) {
            return field === 'low_stock_threshold' ? String(DEFAULT_LOW_STOCK_THRESHOLD) : '';
        }
        return Number.isNaN(parsed) ? `invalid:${raw}` : String(parsed);
    }
    return raw;
}

/** Campos cujo valor canônico mudou entre o salvo e o rascunho. */
export function DiffProductForm(initial, form) {
    const dirty = new Set();
    for (const field of PRODUCT_FORM_FIELDS) {
        if (NormalizeFormValue(field, initial?.[field]) !== NormalizeFormValue(field, form?.[field])) {
            dirty.add(field);
        }
    }
    return dirty;
}

function isFciOrigin(taxOrigin) {
    const value = String(taxOrigin ?? '').trim();
    return value !== '' && FCI_REQUIRED_ORIGINS.includes(Number(value));
}

/**
 * Aplica uma digitação ao rascunho com os efeitos colaterais do campo:
 * GTINs só dígitos (até 14); NCM/CEST mascarados; inteiros só dígitos;
 * origem fora de 3/5/8 limpa o FCI. Título/SKU/unidade ficam como digitados
 * (a tela mostra em maiúsculas por CSS; o payload converte) para o cursor não
 * pular ao editar no meio do texto.
 */
export function ApplyFieldChange(form, field, value) {
    const next = { ...form };
    if (BOOLEAN_FIELDS.has(field)) {
        next[field] = Boolean(value);
        return next;
    }
    const raw = String(value ?? '');
    if (field === 'ean' || field === 'tax_ean' || field === 'case_gtin') {
        next[field] = OnlyDigits(raw).slice(0, 14);
    } else if (field === 'ncm') {
        next[field] = FormatNcm(raw);
    } else if (field === 'cest') {
        next[field] = FormatCest(raw);
    } else if (INTEGER_FIELDS.has(field)) {
        next[field] = raw.replace(/\D/g, '');
    } else {
        next[field] = raw;
    }
    if (field === 'tax_origin' && !isFciOrigin(raw)) {
        next.fci_number = '';
    }
    return next;
}

// ── Validação no cliente ─────────────────────────────────────────────────────

/**
 * Mensagens dos erros de gravação — as MESMAS do backend
 * (`PRODUCT_WRITE_MESSAGES` de `products/productWrite.js`): o 400 do servidor
 * e o aviso da tela dizem a mesma coisa.
 */
export const PRODUCT_FIELD_MESSAGES = {
    TITLE_REQUIRED: 'Informe o nome do produto',
    TITLE_TOO_LONG: `O nome tem no máximo ${PRODUCT_TITLE_MAX} caracteres`,
    BRAND_NOT_IN_LIST: 'Marca fora da lista: escolha uma marca cadastrada',
    UNIT_REQUIRED: 'Informe a unidade',
    UNIT_TOO_LONG: `A unidade tem no máximo ${PRODUCT_UNIT_MAX} caracteres`,
    COST_ZERO: 'Custo zero não é custo: deixe vazio se não souber',
    COST_INVALID: 'Custo inválido',
    COST_DECIMALS: `O custo tem no máximo ${COST_PRICE_DECIMALS} casas decimais`,
    COST_TOO_BIG: 'Custo grande demais',
    SALE_PRICE_INVALID: 'Preço de venda inválido',
    SALE_PRICE_DECIMALS: `O preço de venda tem no máximo ${SALE_PRICE_DECIMALS} casas decimais`,
    SALE_PRICE_TOO_BIG: 'Preço grande demais',
    GTIN_NOT_DIGITS: 'Só dígitos',
    GTIN_LENGTH: 'O GTIN tem 8, 12, 13 ou 14 dígitos',
    TAX_GTIN_14: 'O GTIN tributário não aceita 14 dígitos: use o GTIN-8/12/13 da unidade de varejo',
    TAX_GTIN_LENGTH: 'O GTIN tributário tem 8, 12 ou 13 dígitos',
    CASE_GTIN_LENGTH: 'O GTIN da caixa tem 13 ou 14 dígitos',
    CASE_GTIN_EQUALS_EAN: 'A caixa do fornecedor não pode ter o mesmo GTIN do SKU',
    UNITS_PER_CASE_INVALID: 'Unidades por caixa: número inteiro de 2 a 65535',
    TAX_ORIGIN_INVALID: 'Origem inválida (0 a 8)',
    NCM_INVALID: 'O NCM tem 8 dígitos',
    CEST_INVALID: 'O CEST tem 7 dígitos',
    FCI_FORMAT: 'FCI no formato XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX',
    FCI_ONLY_ORIGIN_358: 'A FCI só vale para origem 3, 5 ou 8',
    WEIGHT_INVALID: 'O peso é maior que zero',
    WEIGHT_DECIMALS: `O peso tem no máximo ${WEIGHT_DECIMALS} casas decimais (grama)`,
    WEIGHT_TOO_BIG: 'Peso grande demais',
    WEIGHT_GROSS_LT_NET: 'O peso bruto não pode ser menor que o líquido',
    UNITS_PER_PALLET_INVALID: 'Unidades por pallet: número inteiro de 1 a 1.000.000',
    LOW_STOCK_INVALID: 'Estoque mínimo: número inteiro de 0 a 1.000.000',
    SUPPLIER_CODE_TOO_LONG: `O código do fornecedor tem no máximo ${SUPPLIER_CODE_MAX} caracteres`,
    SUPPLIER_DESCRIPTION_TOO_LONG: `A descrição do fornecedor tem no máximo ${SUPPLIER_DESCRIPTION_MAX} caracteres`,
};

/** Avisos que NÃO bloqueiam (os dois primeiros são os do backend). */
export const PRODUCT_FIELD_WARNINGS = {
    GTIN_INDICATOR_9: 'Indicador 9 é reservado para medida variável: confira o GTIN-14',
    GTIN14_WITHOUT_TAX_EAN: 'O GTIN-14 não contém o GTIN tributário (a GS1 permite, mas confira)',
    GTIN14_TAX_EMPTY: 'GTIN-14 costuma ser a caixa (DUN-14): informe o GTIN da unidade no GTIN tributário',
    UNITS_PER_CASE_MISSING: 'Informe quantas unidades deste SKU vêm na caixa',
    NO_BRAND: 'Sem marca o produto não entra em ordem de compra',
    BRAND_OUTSIDE_LIST: 'Marca fora da lista de marcas ativas',
    NO_SUPPLIER_CODE: 'A OC exige o código do produto no fornecedor',
    NO_COST: 'Custo pendente: sem custo não há margem nem valor na OC',
    NO_WEIGHT: 'Peso não informado: grava o sentinela de 1 g e fica pendente',
    NO_NET_WEIGHT: 'Peso líquido não informado',
    FCI_MISSING: 'Origem 3, 5 ou 8 leva o Número FCI na NF: a tributação fica incompleta',
};

/** Compatibilidade com quem importa a mensagem de custo zero direto. */
export const COST_ZERO_MESSAGE = PRODUCT_FIELD_MESSAGES.COST_ZERO;

/** "8, 12, 13 ou 14". */
export function JoinLengths(lengths) {
    if (lengths.length <= 1) {
        return String(lengths[0] ?? '');
    }
    return `${lengths.slice(0, -1).join(', ')} ou ${lengths[lengths.length - 1]}`;
}

function gtinErrorMessage(result, lengthMessage) {
    if (result.code === 'NOT_DIGITS') {
        return PRODUCT_FIELD_MESSAGES.GTIN_NOT_DIGITS;
    }
    if (result.code === 'GTIN_LENGTH') {
        return lengthMessage;
    }
    if (result.code === 'GTIN_CHECK_DIGIT') {
        return `Dígito verificador inválido: o último dígito deveria ser ${result.expected}`;
    }
    return null;
}

/** Decimal digitado × regra do campo → código de erro (ou null). */
function decimalErrorCode(raw, { decimals, max, allowZero }) {
    const value = ParseDecimalInput(raw);
    if (value === null) {
        return null;
    }
    if (Number.isNaN(value) || value < 0 || (!allowZero && value === 0)) {
        return 'INVALID';
    }
    if (!HasAtMostDecimals(value, decimals)) {
        return 'DECIMALS';
    }
    return value > max ? 'TOO_BIG' : null;
}

function integerInRange(raw, min, max) {
    const value = ParseIntegerInput(raw);
    return value === null || (!Number.isNaN(value) && value >= min && value <= max);
}

function gtin14Warnings(code, taxEan) {
    if (code.length !== 14) {
        return null;
    }
    if (code.startsWith('9')) {
        return PRODUCT_FIELD_WARNINGS.GTIN_INDICATOR_9;
    }
    if (taxEan.length === 13 && DeriveGtin13FromGtin14(code) !== taxEan) {
        return PRODUCT_FIELD_WARNINGS.GTIN14_WITHOUT_TAX_EAN;
    }
    return null;
}

/**
 * Regras do cliente (espelho de `BuildProductWrite` do backend, com as emendas
 * E2). `{ errors, warnings }` por campo. No modo `new` tudo é validado; no
 * `edit`, só os campos alterados (e o par de uma regra cruzada quando um dos
 * dois mudou) — o fiscal dos produtos antigos está vazio e não pode travar a
 * edição de outro campo.
 *
 * Avisos NUNCA bloqueiam. Obrigatório no create: só SKU e nome (E2); peso,
 * NCM, origem e FCI vazios viram pendência no servidor.
 */
export function ValidateProductForm(form, { mode = 'edit', dirtyFields = new Set(), brands = null } = {}) {
    const M = PRODUCT_FIELD_MESSAGES;
    const W = PRODUCT_FIELD_WARNINGS;
    const errors = {};
    const warnings = {};
    const should = (field) => mode === 'new' || dirtyFields.has(field);
    const filled = (field) => String(form?.[field] ?? '').trim() !== '';

    // Dados gerais
    if (should('title')) {
        const title = UpperCollapse(form.title);
        if (!title) {
            errors.title = M.TITLE_REQUIRED;
        } else if (title.length > PRODUCT_TITLE_MAX) {
            errors.title = M.TITLE_TOO_LONG;
        }
    }
    if (mode === 'new') {
        const skuResult = ValidateNewSku(form.sku);
        if (skuResult.errors.length > 0) {
            errors.sku = skuResult.errors[0].message;
        }
    }
    if (should('unit')) {
        const unit = UpperCollapse(form.unit);
        if (!unit) {
            errors.unit = M.UNIT_REQUIRED;
        } else if (unit.length > PRODUCT_UNIT_MAX) {
            errors.unit = M.UNIT_TOO_LONG;
        }
    }
    if (should('sale_price')) {
        const code = decimalErrorCode(form.sale_price, {
            decimals: SALE_PRICE_DECIMALS,
            max: SALE_PRICE_MAX,
            allowZero: true,
        });
        if (code) {
            errors.sale_price = {
                INVALID: M.SALE_PRICE_INVALID,
                DECIMALS: M.SALE_PRICE_DECIMALS,
                TOO_BIG: M.SALE_PRICE_TOO_BIG,
            }[code];
        }
    }
    if (!filled('brand')) {
        warnings.brand = W.NO_BRAND;
    } else if (Array.isArray(brands) && brands.length > 0 && !brands.some((item) => item?.brand === form.brand)) {
        if (should('brand')) {
            errors.brand = M.BRAND_NOT_IN_LIST;
        } else {
            warnings.brand = W.BRAND_OUTSIDE_LIST;
        }
    }

    // Códigos de barras
    const ean = OnlyDigits(form.ean);
    const taxEan = OnlyDigits(form.tax_ean);
    const caseGtin = OnlyDigits(form.case_gtin);
    const eanResult = ValidateGtin(form.ean, { lengths: GTIN_LENGTHS });
    if (should('ean') && !eanResult.ok) {
        errors.ean = gtinErrorMessage(eanResult, M.GTIN_LENGTH);
    }
    if (eanResult.ok && ean.length === 14) {
        warnings.ean = taxEan ? gtin14Warnings(ean, taxEan) : W.GTIN14_TAX_EMPTY;
        if (!warnings.ean) {
            delete warnings.ean;
        }
    }
    if (should('tax_ean') && taxEan) {
        if (taxEan.length === 14) {
            errors.tax_ean = M.TAX_GTIN_14;
        } else {
            const result = ValidateGtin(form.tax_ean, { lengths: TAX_GTIN_LENGTHS });
            if (!result.ok) {
                errors.tax_ean = gtinErrorMessage(result, M.TAX_GTIN_LENGTH);
            }
        }
    }
    const caseResult = ValidateGtin(form.case_gtin, { lengths: CASE_GTIN_LENGTHS });
    if ((should('case_gtin') || should('ean')) && caseGtin) {
        if (!caseResult.ok) {
            errors.case_gtin = gtinErrorMessage(caseResult, M.CASE_GTIN_LENGTH);
        } else if (caseGtin === ean) {
            errors.case_gtin = M.CASE_GTIN_EQUALS_EAN;
        }
    }
    if (caseResult.ok && caseGtin) {
        const caseWarning = gtin14Warnings(caseGtin, taxEan);
        if (caseWarning) {
            warnings.case_gtin = caseWarning;
        }
    }
    if (should('units_per_case') && !integerInRange(form.units_per_case, UNITS_PER_CASE_MIN, UNITS_PER_CASE_MAX)) {
        errors.units_per_case = M.UNITS_PER_CASE_INVALID;
    }
    if (caseGtin && !filled('units_per_case')) {
        warnings.units_per_case = W.UNITS_PER_CASE_MISSING;
    }

    // Peso e pallet
    const weightMessages = { INVALID: M.WEIGHT_INVALID, DECIMALS: M.WEIGHT_DECIMALS, TOO_BIG: M.WEIGHT_TOO_BIG };
    for (const field of ['weight_net_kg', 'weight_gross_kg']) {
        if (should(field)) {
            const code = decimalErrorCode(form[field], {
                decimals: WEIGHT_DECIMALS,
                max: WEIGHT_MAX_KG,
                allowZero: false,
            });
            if (code) {
                errors[field] = weightMessages[code];
            }
        }
    }
    // Só compara medidas de verdade (vazio = sentinela = "não informado"), como o servidor.
    if (
        (should('weight_net_kg') || should('weight_gross_kg')) &&
        !errors.weight_net_kg &&
        !errors.weight_gross_kg &&
        filled('weight_net_kg') &&
        filled('weight_gross_kg') &&
        ParseDecimalInput(form.weight_gross_kg) < ParseDecimalInput(form.weight_net_kg)
    ) {
        errors.weight_gross_kg = M.WEIGHT_GROSS_LT_NET;
    }
    if (!filled('weight_gross_kg')) {
        warnings.weight_gross_kg = W.NO_WEIGHT;
    } else if (!filled('weight_net_kg')) {
        warnings.weight_net_kg = W.NO_NET_WEIGHT;
    }
    if (
        should('units_per_pallet') &&
        !integerInRange(form.units_per_pallet, UNITS_PER_PALLET_MIN, UNITS_PER_PALLET_MAX)
    ) {
        errors.units_per_pallet = M.UNITS_PER_PALLET_INVALID;
    }

    // Estoque
    if (should('low_stock_threshold') && !integerInRange(form.low_stock_threshold, 0, LOW_STOCK_THRESHOLD_MAX)) {
        errors.low_stock_threshold = M.LOW_STOCK_INVALID;
    }

    // Fornecedor
    if (should('supplier_code') && String(form.supplier_code ?? '').trim().length > SUPPLIER_CODE_MAX) {
        errors.supplier_code = M.SUPPLIER_CODE_TOO_LONG;
    }
    if (!filled('supplier_code')) {
        warnings.supplier_code = W.NO_SUPPLIER_CODE;
    }
    if (
        should('supplier_description') &&
        String(form.supplier_description ?? '').trim().length > SUPPLIER_DESCRIPTION_MAX
    ) {
        errors.supplier_description = M.SUPPLIER_DESCRIPTION_TOO_LONG;
    }
    if (should('cost_price') && filled('cost_price')) {
        if (ParseDecimalInput(form.cost_price) === 0) {
            errors.cost_price = M.COST_ZERO;
        } else {
            const code = decimalErrorCode(form.cost_price, {
                decimals: COST_PRICE_DECIMALS,
                max: COST_PRICE_MAX,
                allowZero: false,
            });
            if (code) {
                errors.cost_price = { INVALID: M.COST_INVALID, DECIMALS: M.COST_DECIMALS, TOO_BIG: M.COST_TOO_BIG }[
                    code
                ];
            }
        }
    }
    if (!filled('cost_price')) {
        warnings.cost_price = W.NO_COST;
    }

    // Tributação
    if (should('tax_origin') && filled('tax_origin')) {
        const origin = Number(form.tax_origin);
        if (!Number.isInteger(origin) || origin < 0 || origin > 8) {
            errors.tax_origin = M.TAX_ORIGIN_INVALID;
        }
    }
    if (should('ncm') && !ValidateNcm(form.ncm).ok) {
        errors.ncm = M.NCM_INVALID;
    }
    if (should('cest') && !ValidateCest(form.cest).ok) {
        errors.cest = M.CEST_INVALID;
    }
    if ((should('fci_number') || should('tax_origin')) && filled('fci_number')) {
        if (!ValidateFci(form.fci_number).ok) {
            errors.fci_number = M.FCI_FORMAT;
        } else if (!isFciOrigin(form.tax_origin)) {
            errors.fci_number = M.FCI_ONLY_ORIGIN_358;
        }
    }
    if (isFciOrigin(form.tax_origin) && !filled('fci_number')) {
        warnings.fci_number = W.FCI_MISSING;
    }

    return { errors, warnings };
}

// ── Payloads ─────────────────────────────────────────────────────────────────

/** Valor do rascunho → valor do wire (número JSON, dígitos, maiúsculas) ou null. */
export function WireValue(field, value) {
    if (BOOLEAN_FIELDS.has(field)) {
        return Boolean(value);
    }
    const raw = String(value ?? '').trim();
    if (!raw) {
        return null;
    }
    if (field === 'sku') {
        return NormalizeSkuInput(raw);
    }
    if (field === 'fci_number') {
        return NormalizeFciNumber(raw);
    }
    if (UPPER_TEXT_FIELDS.has(field)) {
        return UpperCollapse(raw);
    }
    if (TRIM_TEXT_FIELDS.has(field)) {
        return raw;
    }
    if (DIGIT_FIELDS.has(field)) {
        return OnlyDigits(raw) || null;
    }
    if (DECIMAL_FIELDS.has(field)) {
        const parsed = ParseDecimalInput(raw);
        return parsed === null || Number.isNaN(parsed) ? null : parsed;
    }
    if (INTEGER_FIELDS.has(field)) {
        const parsed = ParseIntegerInput(raw);
        return parsed === null || Number.isNaN(parsed) ? null : parsed;
    }
    if (field === 'tax_origin') {
        return Number(raw);
    }
    return raw;
}

/**
 * `tax_ean` que o servidor preencheria sozinho (E2): GTIN do SKU com 8/12/13
 * dígitos e tributário vazio → o tributário é o próprio GTIN. `null` quando
 * não se aplica (GTIN-14 sem tributário é aceito e vira pendência).
 */
export function ResolveTaxEanFill(ean, taxEan) {
    const eanDigits = OnlyDigits(ean);
    if (OnlyDigits(taxEan) || !TAX_GTIN_LENGTHS.includes(eanDigits.length)) {
        return null;
    }
    return ValidateGtin(eanDigits, { lengths: TAX_GTIN_LENGTHS }).ok ? eanDigits : null;
}

/**
 * Corpo do `POST /products`: `sku` e `title` sempre; os demais só quando
 * preenchidos (ausente = padrão do servidor: UN, ACTIVE, estoque mínimo 20,
 * peso sentinela). Sem `format` (o servidor grava SIMPLE; KIT dá 422) e sem os
 * atributos do WMS.
 */
export function BuildCreatePayload(form) {
    const payload = {
        sku: NormalizeSkuInput(form.sku),
        title: UpperCollapse(form.title),
    };
    for (const field of CREATE_FIELDS) {
        if (field === 'title') {
            continue;
        }
        const value = WireValue(field, form[field]);
        if (value !== null) {
            payload[field] = value;
        }
    }
    const taxEanFill = ResolveTaxEanFill(payload.ean, payload.tax_ean);
    if (taxEanFill) {
        payload.tax_ean = taxEanFill;
    }
    return payload;
}

/**
 * Corpo do `PUT /products/:id`: SÓ os campos alterados (o servidor valida só as
 * chaves presentes). Vazio vira `null` (o servidor grava o sentinela no peso);
 * estoque mínimo vazio vai como 20 (a coluna é NOT NULL). Nunca leva `sku`,
 * `format` nem os atributos do WMS.
 */
export function BuildUpdatePayload(form, dirtyFields) {
    const payload = {};
    for (const field of dirtyFields) {
        if (UPDATE_EXCLUDED_FIELDS.has(field) || !PRODUCT_FORM_FIELDS.includes(field)) {
            continue;
        }
        let value = WireValue(field, form[field]);
        if (field === 'low_stock_threshold' && value === null) {
            value = DEFAULT_LOW_STOCK_THRESHOLD;
        }
        payload[field] = value;
    }
    // O preenchimento automático do tributário (E2) espelha o servidor, que só
    // preenche quando a chave `tax_ean` NÃO vem no body: o fill vale quando a
    // mudança veio do `ean` (tributário salvo vazio). Quem esvazia o campo de
    // propósito manda `tax_ean: null`, e o servidor grava NULL.
    if (dirtyFields.has('ean') && !dirtyFields.has('tax_ean')) {
        const taxEanFill = ResolveTaxEanFill(form.ean, form.tax_ean);
        if (taxEanFill) {
            payload.tax_ean = taxEanFill;
        }
    }
    return payload;
}

/** `{ is_bulky?, track_supplier_lot? }` alterados, ou null. */
export function BuildWmsAttributesPatch(form, dirtyFields) {
    const patch = {};
    for (const field of WMS_ATTRIBUTE_FIELDS) {
        if (dirtyFields.has(field)) {
            patch[field] = Boolean(form[field]);
        }
    }
    return Object.keys(patch).length > 0 ? patch : null;
}

// ── Erros do servidor ────────────────────────────────────────────────────────

/**
 * `APIError.details = [{ path, message, code }]` (validate.js / ConflictError)
 * → `{ [campo]: mensagem }` pelo ÚLTIMO segmento do path. A primeira mensagem
 * de cada campo vence.
 */
export function MapServerErrors(details) {
    const out = {};
    if (!Array.isArray(details)) {
        return out;
    }
    for (const item of details) {
        const segments = String(item?.path ?? '')
            .split('.')
            .filter(Boolean);
        const field = segments[segments.length - 1];
        if (!field || field === '(root)' || out[field] || !item?.message) {
            continue;
        }
        out[field] = String(item.message);
    }
    return out;
}

const CONFLICT_FIELD_BY_CODE = {
    SKU_ALREADY_EXISTS: 'sku',
    SKU_DELETED: 'sku',
    GTIN_IN_USE: 'ean',
    BRAND_NOT_IN_LIST: 'brand',
    FCI_ONLY_ORIGIN_358: 'fci_number',
    TAX_GTIN_14: 'tax_ean',
};

/**
 * Erro de gravação (400/409/422) → `{ fieldErrors, formError }`. Detalhe por
 * campo cai no campo; o que não pertence a um campo do formulário (ou vem sem
 * `details`) vira o banner acima das seções.
 */
export function MapSaveError(error) {
    const fieldErrors = {};
    const stray = [];
    const mapped = MapServerErrors(error?.details);
    for (const [field, message] of Object.entries(mapped)) {
        if (PRODUCT_FORM_FIELDS.includes(field)) {
            fieldErrors[field] = message;
        } else {
            stray.push(message);
        }
    }
    const conflictField = CONFLICT_FIELD_BY_CODE[error?.code];
    if (Object.keys(fieldErrors).length === 0 && conflictField && error?.message) {
        fieldErrors[conflictField] = error.message;
    }
    let formError = null;
    if (Object.keys(fieldErrors).length === 0) {
        formError = stray[0] || error?.message || 'Não foi possível salvar';
    } else if (stray.length > 0) {
        formError = stray.join(' · ');
    }
    return { fieldErrors, formError };
}

// ── Pendências e dicas do Bling ──────────────────────────────────────────────

/**
 * Campo em que cada pendência calculada aparece como aviso. As sem campo
 * (FISCAL_INCOMPLETO, BLING_*) ficam só na lista de pendências e no ponto da
 * navegação lateral.
 */
export const PENDING_CODE_FIELDS = {
    SKU_DIFERENTE_DO_BLING: 'sku',
    SKU_SEM_CODIGO_NO_BLING: 'sku',
    SEM_MARCA: 'brand',
    MARCA_FORA_DA_LISTA: 'brand',
    SEM_CODIGO_FORNECEDOR: 'supplier_code',
    CUSTO_PENDENTE: 'cost_price',
    CUSTO_DIFERENTE_DO_BLING: 'cost_price',
    SEM_GTIN: 'ean',
    GTIN_REPETIDO: 'ean',
    GTIN_INVALIDO: 'ean',
    GTIN_CAIXA_NO_EAN: 'ean',
    PESO_NAO_INFORMADO: 'weight_gross_kg',
    SEM_UNIDADES_POR_PALLET: 'units_per_pallet',
};

/**
 * Pendências do detalhe (`pending[]`) → avisos por campo, só nos campos que o
 * usuário AINDA não mexeu (depois de editar, a pendência gravada está velha).
 * `info` vira dica; `warning`/`blocking`, aviso. `SKU_DIFERENTE_DO_BLING`
 * mostra o código do Bling.
 */
export function PendingFieldMessages(pending, { dirtyFields = new Set(), product = null } = {}) {
    const warnings = {};
    const hints = {};
    for (const item of Array.isArray(pending) ? pending : []) {
        const field = PENDING_CODE_FIELDS[item?.code];
        if (!field || dirtyFields.has(field)) {
            continue;
        }
        let message = item.message || item.label || item.code;
        if (item.code === 'SKU_DIFERENTE_DO_BLING' && product?.bling_code) {
            message = `Código no Bling: ${product.bling_code} (ajuste pendente, SKU não renomeado)`;
        }
        const target = item.severity === 'info' ? hints : warnings;
        if (!target[field]) {
            target[field] = message;
        }
    }
    return { warnings, hints };
}

/** Seções que têm pendência do servidor (ponto âmbar na navegação). */
export function PendingSections(pending) {
    const sections = new Set();
    for (const item of Array.isArray(pending) ? pending : []) {
        if (item?.section) {
            sections.add(item.section);
        }
    }
    return sections;
}

function moneyText(value, decimals) {
    return `R$ ${FormatDecimalBr(value, { minFractionDigits: 2, maxFractionDigits: decimals })}`;
}

function differsMoney(formValue, blingValue) {
    const current = ParseDecimalInput(formValue);
    if (current === null || Number.isNaN(current)) {
        return true;
    }
    return Math.abs(current - blingValue) >= 0.005;
}

/**
 * Dicas do `bling_snapshot` (só informativo, E1): por campo, o valor que o
 * Bling tinha e — quando dá para usar — o texto que "Usar valor do Bling"
 * põe no rascunho. Nada é aplicado sem o usuário salvar.
 *
 * `{ [campo]: { label, display, value } }`; `value: null` = só informa
 * (ex.: marca do Bling fora da lista fechada).
 */
export function BlingSnapshotSuggestions(snapshot, form, { brands = [] } = {}) {
    const out = {};
    if (!snapshot || typeof snapshot !== 'object') {
        return out;
    }
    const cost = Number(snapshot.preco_custo);
    if (Number.isFinite(cost) && cost > 0 && differsMoney(form.cost_price, cost)) {
        out.cost_price = {
            label: 'custo do fornecedor',
            display: moneyText(cost, COST_PRICE_DECIMALS),
            value: FormatDecimalBr(cost, {
                minFractionDigits: 2,
                maxFractionDigits: COST_PRICE_DECIMALS,
                grouping: false,
            }),
        };
    }
    const price = Number(snapshot.preco);
    if (Number.isFinite(price) && price > 0 && differsMoney(form.sale_price, price)) {
        out.sale_price = {
            label: 'preço',
            display: `${moneyText(price, SALE_PRICE_DECIMALS)} (não importado)`,
            value: FormatDecimalBr(price, {
                minFractionDigits: SALE_PRICE_DECIMALS,
                maxFractionDigits: SALE_PRICE_DECIMALS,
                grouping: false,
            }),
        };
    }
    const gtin = OnlyDigits(snapshot.gtin);
    if (gtin && gtin !== OnlyDigits(form.ean) && ValidateGtin(gtin, { lengths: GTIN_LENGTHS }).ok) {
        out.ean = { label: 'GTIN', display: gtin, value: gtin };
    }
    const packGtin = OnlyDigits(snapshot.gtin_embalagem);
    if (packGtin && packGtin !== OnlyDigits(form.tax_ean) && ValidateGtin(packGtin, { lengths: TAX_GTIN_LENGTHS }).ok) {
        out.tax_ean = { label: 'GTIN tributário', display: packGtin, value: packGtin };
    }
    const brand = String(snapshot.marca ?? '').trim();
    if (brand && BrandMatchKey(brand) !== BrandMatchKey(form.brand)) {
        const canonical = ResolveCanonicalBrand(brand, brands);
        if (canonical) {
            if (canonical !== form.brand) {
                out.brand = { label: 'marca', display: canonical, value: canonical };
            }
        } else {
            out.brand = { label: 'marca', display: `${brand} (fora da lista)`, value: null };
        }
    }
    return out;
}

// ── KPIs da identidade ───────────────────────────────────────────────────────

/** Preço, custo e margem de referência do RASCUNHO (ao vivo). */
export function ComputePriceKpis(form) {
    const toPositive = (raw) => {
        const value = ParseDecimalInput(raw);
        return value === null || Number.isNaN(value) || value <= 0 ? null : value;
    };
    const price = toPositive(form?.sale_price);
    const cost = toPositive(form?.cost_price);
    const margin = price !== null && cost !== null ? MarginPct(price, cost) : null;
    return { price, cost, margin };
}
